import "./styles.css";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Menu } from "antd";
import { useUserInfo } from "GlobalStates/User";
import { usePageLinks } from "./pagelinks";
import { useLoginWithGoogle } from "Features/Auth/hooks/useGoogleLogin";
import { useMutation } from "@tanstack/react-query";
import { loginUserWithGoogleToken } from "Features/Auth/apis/registerUser";
import apiClient from "QueryClient/axiosClient";
import { useState, useEffect } from "react";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { ROUTES } from "Features/Routes/routeConstants";
import { useRecoilValue } from "recoil";
import { cookieConsentState } from "GlobalStates/CookieConsent";
import { showCustomToast } from "Components/Toast";
import clsx from "clsx";
import { trackFunnelEvent } from "Config/analytics";
import { Flex } from "antd";
import { ProfileModal } from "Features/UserProfile/profilev2";

export const Navbar = ({ collapsed, setCollapsed, isScrolled }) => {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [selectedKey, setSelectedKey] = useState("1");
  const { user, setUser } = useUserInfo();
  const consent = useRecoilValue(cookieConsentState);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const PageLinks = usePageLinks();
  const loginUserMutation = useMutation({
    mutationFn: loginUserWithGoogleToken,
    onSuccess: (data) => {
      trackFunnelEvent("login", {
        method: "google",
        auth_surface: "navbar",
      });
      setUser(() => ({
        token: data?.data?.accessToken,
        tier: data?.data?.tier,
        completedCollegeFinder: data?.data?.completedCollegeFinder,
        name: data?.data?.name,
        picture: data?.data?.picture,
        id: data?.data?.id,
        email: data?.data?.email,
      }));
      if (searchParams?.get("next")) {
        const searchParamsState = {};
        searchParams?.forEach((value, key) => {
          searchParamsState[key] = value;
        });
        navigate(searchParams?.get("next"), {
          state: { ...searchParamsState },
        });
      } else {
        navigate(ROUTES.DASHBOARD);
      }
    },
  });

  const generateGoogleToken = useLoginWithGoogle({
    onSuccessCallback: (user) => {
      trackFunnelEvent("login_start", {
        method: "google",
        auth_surface: "navbar",
      });
      loginUserMutation.mutate({
        token: user?.access_token,
      });
    },
  });

  const logoutUserMutation = useMutation({
    mutationFn: () => {
      return apiClient.post("/auth/logout");
    },
    onSuccess: () => {
      setTimeout(() => {
        navigate(ROUTES.HOME);
        setUser(() => ({
          token: "",
          tier: "",
          completedCollegeFinder: false,
          name: "",
          picture: "",
          id: "",
          email: "",
        }));
      }, 3000);
    },
    onError: (error) => {
      console.error("Logout failed:", error);
      setUser(() => ({
        token: "",
        tier: "",
        completedCollegeFinder: false,
        name: "",
        picture: "",
        id: "",
        email: "",
      }));
      navigate(ROUTES.HOME);
    },
  });

  // Update selected key based on current location
  useEffect(() => {
    const currentPath = location.pathname;
    const matchingLink = PageLinks.find((link) => link.target === currentPath);
    if (matchingLink) {
      setSelectedKey(matchingLink.key);
    } else {
      // Default to home if no match found
      setSelectedKey("1");
    }
  }, [location.pathname, PageLinks]);

  const handleMenuClick = (e) => {
    const { target, external } =
      PageLinks.find((item) => item.key === e.key) || {};
    if (!target) return;
    if (external) {
      window.location.assign(target);
      return;
    }
    navigate(target);
  };

  return user?.token ? (
    <>
      <Menu
        className="navbar-logged-in-menu"
        mode="horizontal"
        items={[
          {
            key: "0",
            className: "sidebar-control",
            icon: collapsed ? (
              <MenuUnfoldOutlined style={{ fontSize: "20px" }} />
            ) : (
              <MenuFoldOutlined style={{ fontSize: "20px" }} />
            ),
          },
          {
            key: "1",
            className: "logout-menu",
            icon: (
              <img
                src={user?.picture}
                className="user-icon"
                alt="user"
                width={40}
                referrerPolicy="no-referrer"
              />
            ),
            children: [
              {
                key: "1-0",
                label: "Profile",
              },
              {
                key: "1-1",
                label: "Logout",
              },
            ],
          },
        ]}
        onClick={(e) => {
          if (e.key === "0") {
            setCollapsed(!collapsed);
          }
          if (e.key === "1-1") {
            logoutUserMutation.mutate();
          }
          if (e.key === "1-0") {
            setIsProfileModalOpen(true);
          }
        }}
        style={{
          border: "none",
        }}
      />
      <ProfileModal
        isModalOpen={isProfileModalOpen}
        setIsModalOpen={setIsProfileModalOpen}
      />
    </>
  ) : (
    <div className={clsx("navbar-main-container", isScrolled && "scrolled")}>
      <div className="navbar-logo-container">
        <img
          src={
            "https://stagingonegrad.blob.core.windows.net/fe-assets/logo.png"
          }
          alt="logo"
          className="navbar-main-logo"
          onClick={() => navigate(ROUTES.HOME)}
        />
      </div>

      <div className="desktop-menu">
        <Menu
          mode="horizontal"
          selectedKeys={[selectedKey]}
          items={PageLinks.map((link) => ({
            key: link.key,
            label: link.label,
          }))}
          className="navbar-menu"
          onClick={handleMenuClick}
          style={{ border: "none", flex: 3 }}
        />
        <div className="button-group">
          <button
            className="signup-button"
            onClick={() => {
              if (!consent) {
                showCustomToast("Please accept cookies to proceed.", {
                  autoClose: 3000,
                });
                return;
              }
              generateGoogleToken();
            }}
          >
            Login
          </button>
          {!location.pathname.includes(ROUTES.COLLEGE_FINDER) && (
            <button
              className="signup-button"
              onClick={() => {
                if (!consent) {
                  showCustomToast("Please accept cookies to proceed.", {
                    autoClose: 3000,
                  });
                  return;
                }
                generateGoogleToken();
              }}
            >
              Sign Up
            </button>
          )}
        </div>
      </div>

      <div
        className="hamburger-icon"
        onClick={() => setIsMobileMenuVisible(true)}
      >
        <MenuOutlined />
      </div>

      {isMobileMenuVisible && (
        <div className="mobile-menu-overlay">
          <Flex
            align="center"
            justify="space-between"
            className="mobile-menu-header"
          >
            <div className="navbar-logo-container mobile-menu-logo">
              <img
                src={
                  "https://stagingonegrad.blob.core.windows.net/fe-assets/logo.png"
                }
                alt="logo"
                className="navbar-main-logo"
                onClick={() => navigate(ROUTES.HOME)}
              />
            </div>
            <button
              className="close-button"
              onClick={() => setIsMobileMenuVisible(false)}
            >
              <CloseOutlined />
            </button>
          </Flex>

          <Menu
            mode="vertical"
            selectedKeys={[selectedKey]}
            className="mobile-nav-links"
            onClick={(e) => {
              if ((e.key === "login" || e.key === "signup") && !consent) {
                showCustomToast("Please accept cookies to proceed.", {
                  autoClose: 3000,
                });
                return;
              }
              if (e.key === "login") {
                generateGoogleToken();
              } else if (e.key === "signup") {
                navigate(ROUTES.COLLEGE_FINDER);
              } else {
                handleMenuClick(e);
              }
              setIsMobileMenuVisible(false);
            }}
            items={[
              ...PageLinks.map((link) => ({
                key: link.key,
                label: link.label,
              })),
            ]}
          />
          <div className="mobile-button-group">
            <button
              className="signup-button mobile-login-button"
              onClick={() => {
                if (!consent) {
                  showCustomToast("Please accept cookies to proceed.", {
                    autoClose: 3000,
                  });
                  return;
                }
                generateGoogleToken();
                setIsMobileMenuVisible(false);
              }}
            >
              Login
            </button>
            <button
              className="signup-button mobile-login-button"
              onClick={() => {
                generateGoogleToken();
                setIsMobileMenuVisible(false);
              }}
            >
              Sign Up
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
